// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { linkIconStyleGenerator } from "@bg/App/SharedStyles/GeneralStyles";
import { ProductAvailabilityStyles, productAvailabilityStyles } from "@bg/Overrides/Components/ProductAvailability";
import { ProductImageStyles } from "@bg/Overrides/Components/ProductImage";
import { ProductPriceStyles, productPriceStyles } from "@bg/Overrides/Components/ProductPrice";
import { ProductQtyOrderedStyles } from "@bg/Overrides/Components/ProductQuantityOrdered";
import { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import { ProductImageBadgesStyles } from "@insite/content-library/Components/ProductImageBadges";
import { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import { ProductTextBadgesStyles } from "@insite/content-library/Components/ProductTextBadges";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import { IconPresentationProps } from "@insite/mobius/Icon";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { SelectPresentationProps } from "@insite/mobius/Select";
import { TextFieldPresentationProps } from "@insite/mobius/TextField";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { css } from "styled-components";

export interface MyListsDetailsProductListLineStyles {
    lineInnerContainer?: InjectableCss;
    leftSection?: InjectableCss;
    rightSection?: InjectableCss;
    rightSectionTop?: InjectableCss;
    imageGridItem?: InjectableCss;
    dragGrid?: InjectableCss;
    selectedCheckbox?: CheckboxPresentationProps;
    dragHandleIcon?: IconPresentationProps;
    dragIconWrapper?: InjectableCss;
    productImageStyles?: ProductImageStyles;
    descriptionGridItem?: InjectableCss;
    productDescriptionStyles?: ProductDescriptionStyles;
    productPartNumbersStyles?: ProductPartNumbersStyles;
    partNumbersGridItem?: InjectableCss;
    availabilityGridItem?: InjectableCss;
    productAvailabilityStyles?: ProductAvailabilityStyles;
    printQrCode?: LinkPresentationProps;
    priceAndAvailabilityInnerContainer?: InjectableCss;
    priceGridItem?: InjectableCss;
    productPriceStyles?: ProductPriceStyles;
    uomAndQuantityInnerContainer?: InjectableCss;
    uomGridItem?: InjectableCss;
    productUnitOfMeasureSelectStyles?: SelectPresentationProps;
    quantityGridItem?: InjectableCss;
    productQuantityOrderedStyles?: ProductQtyOrderedStyles;
    lineTotal?: InjectableCss;
    priceTotal?: TypographyPresentationProps;
    notesGridItem?: InjectableCss;
    buttonsGridItem?: InjectableCss;
    addToCartButton?: ButtonPresentationProps;
    deleteLink?: ButtonPresentationProps;
    updateQuantityWrapper?: InjectableCss;
    updateQuantityLink?: LinkPresentationProps;
    quantityUpdatedIcon?: IconPresentationProps;
    quantityUpdatedText?: TypographyPresentationProps;
    restrictedDescriptionGridItem?: InjectableCss;
    restrictedDescriptionText?: TypographyPresentationProps;
    restrictedMessageText?: TypographyPresentationProps;
    restrictedRemoveItemLink?: LinkPresentationProps;
    textBadgeStyles?: ProductTextBadgesStyles;
    imageBadgeStyles?: ProductImageBadgesStyles;
}

export const productListLineStyles: MyListsDetailsProductListLineStyles = {
    lineInnerContainer: {
        css: css`
            display: flex;
            padding: 15px;
            position: relative;
            background: ${getColor("common.white")};
            z-index: 2;

            legend {
                display: none;
            }

            @media (max-width: 1301px) {
                flex-direction: column;
                align-items: flex-start;
                legend {
                    display: block;
                    font-size: 10px;
                    line-height: 13px;
                    text-transform: capitalize;
                    font-weight: 400;
                }
                & > * {
                    width: 100%;
                    &:first-child {
                        margin-bottom: 20px;
                    }
                }
            }

            @media print {
                flex-direction: column;
                align-items: flex-start;
                legend {
                    display: block;
                    font-size: 10px;
                    line-height: 13px;
                    text-transform: capitalize;
                    font-weight: 400;
                }
                & > * {
                    width: 100%;
                    &:first-child {
                        margin-bottom: 20px;
                    }
                }
            }

            &&&.no-borders {
                border-right: 3px solid ${getColor("common.ui3")};
                border-left: 3px solid ${getColor("common.ui3")};
            }

            &&.last-line {
                border-bottom: 3px solid ${getColor("common.ui3")} !important;
            }

            &&.has-related-products {
                border-bottom: 3px solid ${getColor("common.ui3")};
            }

            &&&.related-item {
                margin-left: 80px;
                border: 3px solid #dddde5;
                border-top: 0;
                z-index: 1;

                @media (max-width: 600px) {
                    margin-left: 50px;
                }

                &:before {
                    position: absolute;
                    content: " ";
                    width: 39px;
                    height: calc(100% + 4px);
                    border-bottom: 3px dashed #dddde5;
                    border-left: 3px dashed #dddde5;
                    border-radius: 9px;
                    left: -39px;
                    top: -50%;
                    z-index: -1;
                    @media (max-width: 600px) {
                        width: 30px;
                        left: -30px;
                    }
                }

                &:last-child {
                    border-bottom: 0;
                }

                & > div:first-child {
                    max-width: 614px;
                }
            }
            page-break-inside: avoid;
        `,
    },
    leftSection: {
        css: css`
            flex-grow: 1;
            max-width: 695px;
            display: flex;
            @media (max-width: 600px) {
                flex-direction: column;
                align-items: flex-start;
            }
        `,
    },
    rightSection: {
        css: css`
            flex: 1;
            display: flex;
            flex-direction: column;
            justify-content: space-between;
        `,
    },
    imageGridItem: {
        css: css`
            display: flex;
            max-width: 179px;
            width: 100%;
            margin-right: 15px;
            &.related-item {
                max-width: 139px;
            }
            @media (max-width: 481px) {
                margin: 0;
                max-width: 100%;
            }
        `,
    },
    dragGrid: {
        css: css`
            margin-right: 15px;
            display: flex;
            flex-direction: column;
            @media print {
                display: none;
            }
            @media (max-width: 481px) {
                margin: 0;
            }
        `,
    },
    dragIconWrapper: {
        css: css`
            flex: 1;
            display: flex;
            justify-content: center;
            align-items: center;
            @media print {
                display: none;
            }
        `,
    },
    dragHandleIcon: {
        src: "Drag",
        color: "secondary",
        size: 17,
        css: css`
            cursor: move;
            opacity: 0.7;
            @media (max-width: 1025px) {
                display: none;
            }
        `,
    },
    productImageStyles: {
        linkWrappingImage: {
            css: css`
                justify-content: center;
                img {
                    object-fit: contain;
                }
            `,
        },
        image: {
            cloudinary: true,
            cloudinaryWidth: "139",
            disableLazyLoad: true,
            height: "139px",
        },
    },
    descriptionGridItem: {
        css: css`
            flex: 1;
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            padding-right: 20px;
            @media (max-width: 600px) {
                margin-top: 20px;
                min-width: 100%;
                padding: 0;
            }
        `,
    },
    productDescriptionStyles: {
        productDetailLink: {
            typographyProps: {
                css: css`
                    opacity: 0.7;
                    font-size: 18px;
                    line-height: 22px;
                    height: 44px;
                `,
            },
        },
        productDetailDescription: {
            css: css`
                opacity: 0.7;
                font-size: 18px;
                line-height: 22px;
                height: 44px;
            `,
        },
    },
    partNumbersGridItem: {
        css: css`
            margin-top: 10px;
            @media (max-width: 481px) {
                margin-bottom: 10px;
            }
        `,
    },
    availabilityGridItem: {
        css: css`
            display: flex;
            flex-grow: 1;
            align-items: flex-end;
            justify-content: space-between;
            @media (max-width: 481px) {
                flex-wrap: wrap;
            }
        `,
    },
    productAvailabilityStyles: {
        container: {
            css: css`
                flex-grow: 1;
                display: flex;
                align-items: center;
                @media (max-width: 481px) {
                    width: 100%;
                    margin-bottom: 20px;
                }
            `,
        },
        nonStockText: {
            css: css`
                ${productAvailabilityStyles.nonStockText?.css}
                max-width: 400px;
            `,
        },
    },
    printQrCode: linkIconStyleGenerator("QrCode", "13px", 15, "opacity: 0.8;line-height: 16px;"),
    rightSectionTop: {
        css: css`
            display: flex;
            align-items: center;
            @media (max-width: 7468px) {
                flex-wrap: wrap;
            }
        `,
    },
    priceAndAvailabilityInnerContainer: {
        css: css`
            display: flex;
            flex: 1;
            @media (max-width: 600px) {
                flex-wrap: wrap;
            }
        `,
    },
    priceGridItem: {
        css: css`
            flex: 1;
            display: flex;
            align-items: center;
            padding-right: 5px;
            @media (max-width: 600px) {
                min-width: 50%;
                order: 2;
            }
        `,
    },
    productPriceStyles: {
        wrapper: {
            css: css`
                display: flex;
                flex-direction: column;
                width: 100%;
                @media (max-width: 1301px) {
                    align-items: flex-start;
                }
            `,
        },
        errorWrapper: {
            css: css`
                font-size: 13px;
            `,
        },
        priceWrapper: {
            css: css`
                text-align: center;
                @media (max-width: 481px) {
                    text-align: left;
                }
            `,
        },
        priceLabelText: {
            css: css`
                display: none;
            `,
        },
        price: {
            priceText: {
                size: "18px",
                lineHeight: "22px",
                weight: "bold",
            },
            nonStockText: {
                css: css`
                    ${productPriceStyles.price?.nonStockText?.css}
                    max-width: 180px;
                `,
            },
        },
    } as ProductPriceStyles,
    uomAndQuantityInnerContainer: {
        css: css`
            flex: 1;
            max-width: 125px;
            @media (max-width: 600px) {
                order: 3;
                min-width: 50%;
                display: flex;
                justify-content: flex-end;
                && > div {
                    max-width: 130px;
                }
            }
        `,
    },
    lineTotal: {
        css: css`
            flex: 1;
            display: flex;
            align-items: center;
            justify-content: flex-end;
            padding-left: 5px;
            @media (max-width: 1301px) {
                flex-direction: column;
                align-items: flex-end;
            }
            @media (max-width: 600px) {
                padding-left: 0px;
                align-items: flex-start;
                justify-content: flex-start;
                margin-bottom: 10px;
                order: 1;
                min-width: 100%;
            }
        `,
    },
    priceTotal: {
        size: "18px",
        lineHeight: "22px",
        weight: "bold",
        css: css`
            word-break: break-all;
            legend {
                text-align: right;
            }
        `,
    },
    buttonsGridItem: {
        css: css`
            width: 210px;
            display: flex;
            justify-content: space-between;
            padding-left: 20px;
            &&.one-button {
                width: 150px;
                justify-content: flex-end;
            }
            && .default,
            && .loading {
                min-height: 32px;
                font-weight: 500;
                min-width: 135px;
                @media (max-width: 500px) {
                    min-width: 0px;
                }
                .atc-label {
                    font-size: 14px;
                    line-height: 16px;
                }
            }

            @media (max-width: 500px) {
                width: 100% !important;
                padding: 0;
                margin: 10px 0;
                align-items: center;
            }
        `,
    },
    deleteLink: {
        css: css`
            &&& {
                min-width: auto;
                background: transparent;
                padding: 0;
                min-height: unset;
                svg g {
                    fill: ${getColor("secondary")};
                }

                &:hover svg g {
                    fill: ${getColor("primary")};
                }
            }
        `,
    },
    notesGridItem: {
        css: css`
            width: 100%;
            margin: 10px 0px 0;
        `,
    },
    restrictedMessageText: {
        color: "danger.main",
    },
    textBadgeStyles: {
        container: {
            css: css`
                width: 60%;
                margin-bottom: 5px;
            `,
        },
    },
    imageBadgeStyles: {
        container: {
            css: css`
                width: 60%;
                margin-bottom: 5px;
            `,
        },
        imageItem: {
            css: css`
                margin: 5px 0;
            `,
        },
    },
};
